import { useEffect, useRef, useState } from 'react';
import { useReveal } from '../lib/anim/useReveal';
import { useCountUp } from '../lib/anim/useCountUp';

/* ─── Inline styles / keyframes ─────────────────────────────────────────── */
const EARN_CSS = `
@keyframes earn-blink {
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
}
@keyframes earn-stripe-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
}
@keyframes earn-dash {
  to { stroke-dashoffset: 0; }
}
.earn-cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  background: var(--color-accent);
  vertical-align: text-bottom;
  animation: earn-blink 1s step-end infinite;
  margin-left: 1px;
}
.earn-stripe-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-accent);
  display: inline-block;
  flex-shrink: 0;
  animation: earn-stripe-pulse 1.5s ease-in-out infinite;
}
.earn-stripe-dot.inactive {
  background: var(--color-mute);
  animation: none;
  opacity: 0.4;
}
.earn-preview-card {
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.28s ease-out, transform 0.28s ease-out;
}
.earn-preview-card.visible {
  opacity: 1;
  transform: translateY(0);
}
.earn-caption-pill {
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.28s ease-out, transform 0.28s ease-out;
}
.earn-caption-pill.visible {
  opacity: 1;
  transform: translateY(0);
}
.earn-chart-line {
  stroke-dashoffset: var(--earn-total-length, 1200px);
  animation-fill-mode: forwards;
  animation-timing-function: ease-out;
}
.earn-chart-line.drawing {
  animation: earn-dash var(--earn-draw-dur, 4.5s) ease-out forwards;
}
@media (prefers-reduced-motion: reduce) {
  .earn-cursor { animation: none; opacity: 1; }
  .earn-stripe-dot.inactive { background: var(--color-accent); animation: earn-stripe-pulse 1.5s ease-in-out infinite; opacity: 1; }
  .earn-preview-card { opacity: 1 !important; transform: none !important; transition: none !important; }
  .earn-caption-pill { opacity: 1 !important; transform: none !important; transition: none !important; }
  .earn-chart-line { stroke-dashoffset: 0 !important; animation: none !important; }
}
`;

let earnCSSInjected = false;
function injectEarnCSS() {
  if (earnCSSInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = EARN_CSS;
  document.head.appendChild(style);
  earnCSSInjected = true;
}

const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

/* ─── Revenue curve data ──────────────────────────────────────────────────
   Smooth easeOutCubic-ish path from (1,$0) to (30,$1937).
*/
const CHART_W = 580;
const CHART_H = 180;
const PAD_L = 52;
const PAD_R = 16;
const PAD_T = 24;
const PAD_B = 32;

const PLOT_W = CHART_W - PAD_L - PAD_R;
const PLOT_H = CHART_H - PAD_T - PAD_B;

const MAX_REVENUE = 2000;
const MAX_DAY = 30;

function revenueAt(day: number): number {
  const t = (day - 1) / (MAX_DAY - 1);
  const eased = t < 0.25
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2;
  return Math.round(eased * 1937);
}

function dayToX(day: number): number {
  return PAD_L + ((day - 1) / (MAX_DAY - 1)) * PLOT_W;
}

function revenueToY(rev: number): number {
  return PAD_T + PLOT_H - (rev / MAX_REVENUE) * PLOT_H;
}

function buildPath(): string {
  const points: Array<[number, number]> = [];
  for (let d = 1; d <= 30; d++) {
    points.push([dayToX(d), revenueToY(revenueAt(d))]);
  }
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx = (prev[0] + curr[0]) / 2;
    d += ` C ${cpx} ${prev[1]} ${cpx} ${curr[1]} ${curr[0]} ${curr[1]}`;
  }
  return d;
}

const PATH_LENGTH = 1200;

/* ─── Milestone markers ──────────────────────────────────────────────────── */
const MILESTONES = [
  { day: 4,  revenue: 149,  label: 'first Pilot customer · $149',  delayMs: 4000 },
  { day: 11, revenue: 447,  label: '30-day CFA gate · $447',       delayMs: 5500 },
  { day: 22, revenue: 1490, label: 'Phase 2 territory · $1,490',   delayMs: 7000 },
];

/* ─── Component ──────────────────────────────────────────────────────────── */
export function MockupEarn() {
  const sectionHeading = useReveal();
  const bodyReveal = useReveal({ delay: 1 });
  const mockupReveal = useReveal({ delay: 2 });

  const stageRef = useRef<HTMLDivElement>(null);
  const hasPlayed = useRef(false);

  const TYPED_TEXT = 'Audit dbt models for analytics teams stuck in spreadsheet hell';
  const [typedChars, setTypedChars] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [stripeDotActive, setStripeDotActive] = useState(false);

  const [chartDrawing, setChartDrawing] = useState(false);
  const [milestoneVisible, setMilestoneVisible] = useState([false, false, false]);
  const [countUpStarted, setCountUpStarted] = useState(false);

  const mrrCountUp = useCountUp<HTMLSpanElement>(countUpStarted ? 1937 : 0, {
    prefix: '$',
    format: 'comma',
    duration: 4500,
  });

  useEffect(() => {
    injectEarnCSS();
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const noIO =
      typeof window === 'undefined' ||
      !('IntersectionObserver' in window);

    if (prefersReduced || noIO) {
      setTypedChars(TYPED_TEXT.length);
      setShowPreview(true);
      setStripeDotActive(true);
      setChartDrawing(true);
      setMilestoneVisible([true, true, true]);
      setCountUpStarted(true);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasPlayed.current) {
          hasPlayed.current = true;
          io.unobserve(entry.target);
          playSequence();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function playSequence() {
    await wait(400);
    const charDelay = 35;
    for (let i = 1; i <= TYPED_TEXT.length; i++) {
      setTypedChars(i);
      await wait(charDelay);
    }
    await wait(300);
    setShowPreview(true);
    await wait(200);
    setStripeDotActive(true);
    await wait(300);
    setChartDrawing(true);
    setCountUpStarted(true);
    await wait(800);
    setMilestoneVisible((prev) => [true, prev[1], prev[2]]);
    await wait(1500);
    setMilestoneVisible((prev) => [prev[0], true, prev[2]]);
    await wait(1500);
    setMilestoneVisible((prev) => [prev[0], prev[1], true]);
  }

  const svgPath = buildPath();
  const chartWidthFull = CHART_W;

  return (
    <section
      className="py-24"
      style={{ background: 'var(--color-bg)', borderTop: '1px solid var(--color-hairline)' }}
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section heading */}
        <div
          ref={sectionHeading.ref}
          {...sectionHeading.props}
          className={`mb-4${sectionHeading.props.className ? ' ' + sectionHeading.props.className : ''}`}
        >
          <p className="eyebrow" data-r>
            <span className="eyebrow-dash" />
            <span>04 · EARN</span>
          </p>
          <h2
            className="text-2xl md:text-3xl font-bold max-w-xl"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--color-ink)' }}
          >
            Type the idea. Watch it earn.
          </h2>
        </div>

        <div
          ref={bodyReveal.ref}
          {...bodyReveal.props}
          className={`mb-10${bodyReveal.props.className ? ' ' + bodyReveal.props.className : ''}`}
        >
          <p
            className="text-base max-w-prose"
            style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
          >
            Onwards&rsquo; six engines are paid off. Your wrap takes a weekend. From there it&rsquo;s just whether the idea works.
          </p>
        </div>

        {/* Mockup card */}
        <div
          ref={(el) => {
            (mockupReveal.ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
            (stageRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }}
          {...mockupReveal.props}
          className={`max-w-3xl${mockupReveal.props.className ? ' ' + mockupReveal.props.className : ''}`}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-hairline)',
            borderRadius: '20px',
            padding: '32px',
          }}
        >
          {/* Prompt input */}
          <div
            style={{
              background: 'var(--color-bg)',
              border: '1px solid var(--color-hairline)',
              borderRadius: '10px',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minHeight: '44px',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '14px',
                color: 'var(--color-accent)',
                fontWeight: 600,
                flexShrink: 0,
                userSelect: 'none',
              }}
            >
              &gt;
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '13px',
                color: 'var(--color-ink)',
                flex: 1,
                lineHeight: 1.5,
              }}
            >
              {TYPED_TEXT.slice(0, typedChars)}
              {typedChars < TYPED_TEXT.length && <span className="earn-cursor" aria-hidden="true" />}
            </span>
          </div>

          {/* Live product preview card */}
          <div
            className={`earn-preview-card${showPreview ? ' visible' : ''}`}
            style={{ marginTop: '12px' }}
          >
            <div
              style={{
                background: 'var(--color-bg)',
                border: '1px solid var(--color-hairline)',
                borderRadius: '10px',
                padding: '12px 16px',
              }}
            >
              {/* Top row: product name + Active pill (indigo) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '15px',
                    fontWeight: 600,
                    color: 'var(--color-ink)',
                  }}
                >
                  DbtScore
                </span>
                <span
                  style={{
                    background: 'var(--color-accent)',
                    color: '#FFFFFF',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '10px',
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: '999px',
                    letterSpacing: '0.04em',
                  }}
                >
                  Active
                </span>
              </div>

              {/* Subdomain */}
              <div style={{ marginBottom: '8px' }}>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '11px',
                    color: 'var(--color-mute)',
                  }}
                >
                  dbt-score.clarity.app
                </span>
              </div>

              {/* Meta row */}
              <div
                style={{
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '11px',
                    color: 'var(--color-mute)',
                  }}
                >
                  Engine: Nexlytics Scorer
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '11px',
                    color: 'var(--color-mute)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  Stripe Connect:
                  <span
                    className={`earn-stripe-dot${stripeDotActive ? '' : ' inactive'}`}
                    aria-label={stripeDotActive ? 'connected' : 'connecting'}
                  />
                </span>
              </div>
            </div>
          </div>

          {/* Revenue chart panel */}
          <div
            style={{
              marginTop: '20px',
              background: 'var(--color-bg)',
              border: '1px solid var(--color-hairline)',
              borderRadius: '10px',
              padding: '16px',
              position: 'relative',
            }}
          >
            {/* Top row: label + big MRR number (GOLD placement 3: $1,937 under-band) */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '12px',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '10px',
                  fontWeight: 600,
                  color: 'var(--color-mute)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                30-day MRR
              </span>
              {/* GOLD placement 3: $1,937 MRR callout — gold under-band via wrapping span */}
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '28px',
                  fontWeight: 700,
                  color: 'var(--color-ink)',
                  lineHeight: 1,
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                <span ref={mrrCountUp.ref}>
                  {countUpStarted ? mrrCountUp.value : '$0'}
                </span>
                {/* Gold under-band behind lower third */}
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: '2px',
                    height: '3px',
                    background: 'var(--color-gold)',
                    opacity: 0.8,
                    zIndex: 0,
                    display: 'block',
                  }}
                />
              </span>
            </div>

            {/* SVG chart */}
            <div style={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
              <svg
                viewBox={`0 0 ${chartWidthFull} ${CHART_H}`}
                width="100%"
                style={{ display: 'block', minWidth: '320px' }}
                aria-label="30-day revenue chart: $0 climbing to $1,937 across days 1-30 with milestone markers at days 4, 11, and 22."
                role="img"
              >
                {/* Y-axis hairline rules + labels */}
                {[0, 500, 1000, 1500, 2000].map((rev) => {
                  const y = revenueToY(rev);
                  return (
                    <g key={rev}>
                      <line
                        x1={PAD_L}
                        y1={y}
                        x2={CHART_W - PAD_R}
                        y2={y}
                        stroke="var(--color-hairline)"
                        strokeWidth="1"
                      />
                      <text
                        x={PAD_L - 6}
                        y={y + 4}
                        textAnchor="end"
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '9px',
                          fill: 'var(--color-mute)',
                        }}
                      >
                        {rev === 0 ? '$0' : `$${(rev / 1000).toFixed(0)}k`}
                      </text>
                    </g>
                  );
                })}

                {/* X-axis ticks + labels */}
                {[5, 10, 15, 20, 25, 30].map((day) => {
                  const x = dayToX(day);
                  return (
                    <g key={day}>
                      <line
                        x1={x}
                        y1={PAD_T + PLOT_H}
                        x2={x}
                        y2={PAD_T + PLOT_H + 4}
                        stroke="var(--color-hairline)"
                        strokeWidth="1"
                      />
                      <text
                        x={x}
                        y={PAD_T + PLOT_H + 14}
                        textAnchor="middle"
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '9px',
                          fill: 'var(--color-mute)',
                        }}
                      >
                        {day}
                      </text>
                    </g>
                  );
                })}

                {/* X-axis label */}
                <text
                  x={PAD_L}
                  y={PAD_T + PLOT_H + 28}
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '9px',
                    fill: 'var(--color-mute)',
                  }}
                >
                  day
                </text>

                {/* Milestone vertical hash marks */}
                {MILESTONES.map((m) => {
                  const x = dayToX(m.day);
                  const y0 = revenueToY(m.revenue);
                  return (
                    <line
                      key={m.day}
                      x1={x}
                      y1={y0}
                      x2={x}
                      y2={PAD_T + PLOT_H}
                      stroke="var(--color-hairline)"
                      strokeWidth="1"
                      strokeDasharray="2 2"
                    />
                  );
                })}

                {/* GOLD placement 1: revenue chart line — gold stroke 2.5px */}
                <path
                  d={svgPath}
                  fill="none"
                  stroke="var(--color-gold)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  pathLength={PATH_LENGTH}
                  strokeDasharray={PATH_LENGTH}
                  className={`earn-chart-line${chartDrawing ? ' drawing' : ''}`}
                  style={{
                    ['--earn-total-length' as string]: `${PATH_LENGTH}`,
                    ['--earn-draw-dur' as string]: '4.5s',
                  }}
                />
              </svg>

              {/* GOLD placement 2: Caption pills — gold-soft bg + gold-deep left-border */}
              {MILESTONES.map((m, i) => {
                const x = dayToX(m.day);
                const y = revenueToY(m.revenue);
                const leftPct = (x / chartWidthFull) * 100;
                const topPct = (y / CHART_H) * 100;

                return (
                  <div
                    key={m.day}
                    className={`earn-caption-pill${milestoneVisible[i] ? ' visible' : ''}`}
                    style={{
                      position: 'absolute',
                      left: `${leftPct}%`,
                      top: `calc(${topPct}% - 32px)`,
                      transform: 'translateX(-50%)',
                      background: 'var(--color-gold-soft)',
                      borderLeft: '3px solid var(--color-gold-deep)',
                      borderRadius: '0 4px 4px 0',
                      padding: '3px 8px',
                      whiteSpace: 'nowrap',
                      zIndex: 2,
                      pointerEvents: 'none',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '10px',
                        color: 'var(--color-ink)',
                      }}
                    >
                      {m.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <span id="thread-end" aria-hidden />
      </div>
    </section>
  );
}
